import { addMonths } from 'date-fns/addMonths'
import { format } from 'date-fns/format'
import { getDay } from 'date-fns/getDay'
import { getDaysInMonth } from 'date-fns/getDaysInMonth'
import { isBefore } from 'date-fns/isBefore'
import { isSameMonth } from 'date-fns/isSameMonth'
import { startOfMonth } from 'date-fns/startOfMonth'
import type { ReactNode } from 'react'
import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useFormContext } from 'react-hook-form'
import { Pressable, Text, View } from 'react-native'
import { tv } from 'tailwind-variants'

import { useQuoteData } from '@flows-ui/services/QuoteDataContext'
import type { PremiumInterval } from '@flows-ui/services/QuoteDataContext.types'
import { useTranslation } from '@flows-ui/utils/i18n'
import { EVENTS } from '@flows/configs/analytics/constants'
import type { TextContent } from '@flows/core/i18n/types'
import type { ProductKey } from '@flows/index'
import { createSafeDate } from '@flows/insurance/utils/dateUtils'
import {
  clampToEarliestStartDate,
  getEarliestStartDate,
} from '@flows/insurance/utils/startDateUtils'
import { body1, body2, caption, h4 } from '@ui-kit/core/typography'

import { Price } from './Price'
import { QuoteHeader } from './QuoteHeader'

const startDateStyles = tv({
  slots: {
    container: 'mt-6 w-full flex-row flex-wrap items-center justify-center gap-3 pt-2',
    label: h4({ color: 'deepForest', className: 'font-medium' }),
    dateButton: 'rounded-xl bg-pale-spring-light px-4 py-2',
    dateText: h4(),
    backdrop: 'fixed inset-0 z-[9999] flex items-center justify-center bg-black/50',
    modal: 'w-[90%] max-w-[380px] rounded-2xl bg-white p-6',
    modalHeader: 'mb-4 flex-row items-center justify-between',
    modalTitle: h4(),
    closeIcon: h4(),
    calendarCard: 'rounded-lg border border-grey-200 p-4',
    monthYearRow: 'mb-4 flex-row items-center gap-3',
    navArrow: 'px-2 text-xl',
    fieldCard: 'flex-1 rounded-lg border border-grey-300 p-3',
    fieldLabel: caption({ color: 'grey500' }),
    fieldValue: body1(),
    weekdayRow: 'mb-2 flex-row',
    weekdayLabel: caption({ color: 'grey500', align: 'center', className: 'flex-1' }),
    calendarGrid: 'flex-row flex-wrap',
    emptyCell: 'aspect-square w-[14.28%]',
    dayCell: 'aspect-square w-[14.28%] items-center justify-center rounded-full',
    confirmButton: 'mt-4 items-center rounded-lg bg-black py-4',
    confirmText: body2({ bold: true, color: 'white' }),
  },
  variants: {
    selected: {
      true: { dayCell: 'bg-black' },
      false: { dayCell: 'bg-transparent' },
    },
  },
})

const headerStyles = tv({
  slots: {
    container: 'items-center',
    quoteHeaderWrapper: 'pb-6',
  },
})

type Props = {
  productName: string
  productKey: ProductKey
  headerTitle?: TextContent
  headerSubtitle?: TextContent
  allowedPremiumIntervals?: readonly PremiumInterval[]
}

type StartDateTriggerProps = { displayDate: string; onPress: () => void }

const DefaultStartDateTrigger = ({ displayDate, onPress }: StartDateTriggerProps) => {
  const { t } = useTranslation()
  const styles = startDateStyles()
  return (
    <View className={styles.container()}>
      <Text className={styles.label()}>{t('sharedFlows_common:steps.quote.startDate.label')}</Text>
      <Pressable className={styles.dateButton()} onPress={onPress}>
        <Text className={styles.dateText()}>{displayDate} ▾</Text>
      </Pressable>
    </View>
  )
}

const getFirstSelectableDay = (earliestStartDate: Date, year: number, month: number) =>
  year === earliestStartDate.getFullYear() && month === earliestStartDate.getMonth() + 1
    ? earliestStartDate.getDate()
    : 1

const getMondayFirstOffset = (weekday: number) => (weekday === 0 ? 6 : weekday - 1)

export const InsuranceStartDate = ({
  productKey,
  renderTrigger = DefaultStartDateTrigger,
}: {
  productKey: ProductKey
  renderTrigger?: (props: StartDateTriggerProps) => ReactNode
}) => {
  const { t } = useTranslation()
  const { trackEvent } = useQuoteData()
  const { watch, setValue } = useFormContext()
  const [showModal, setShowModal] = useState(false)

  const openModal = () => {
    setShowModal(true)
    trackEvent(EVENTS.PopupOpened, { id: 'QuoteHeaderDateSelectorModal' })
  }

  const startDates = watch(`startDates.${productKey}`) ?? {}
  const { year, month, day } = startDates

  const earliestStartDate = getEarliestStartDate(productKey)
  const earliestStartDateMs = earliestStartDate.getTime()

  const persistedDate = year && month && day ? createSafeDate(year, month, day) : undefined
  const initialDate = clampToEarliestStartDate(persistedDate, productKey)

  const [selYear, setSelYear] = useState(String(initialDate.getFullYear()))
  const [selMonth, setSelMonth] = useState(String(initialDate.getMonth() + 1))
  const [selDay, setSelDay] = useState(String(initialDate.getDate()))

  const selectedMonthDate = createSafeDate(selYear, selMonth, 1)

  const isAtCurrentMonth = isSameMonth(selectedMonthDate, earliestStartDate)

  const navigateMonth = (delta: number) => {
    const next = addMonths(selectedMonthDate, delta)
    if (isBefore(next, startOfMonth(earliestStartDate))) {
      return
    }
    const y = next.getFullYear()
    const m = next.getMonth() + 1
    setSelYear(String(y))
    setSelMonth(String(m))
    setSelDay(String(getFirstSelectableDay(earliestStartDate, y, m)))
  }
  const displayDate = format(createSafeDate(selYear, selMonth, selDay), 'dd/MM/yyyy')

  useEffect(() => {
    const persistedIsUsable =
      year && month && day && !isBefore(createSafeDate(year, month, day), earliestStartDateMs)
    if (persistedIsUsable) {
      return
    }
    setValue(`startDates.${productKey}`, { year: selYear, month: selMonth, day: selDay })
  }, [day, month, selDay, selMonth, selYear, setValue, year, productKey, earliestStartDateMs])

  const daysInMonth = getDaysInMonth(selectedMonthDate)
  const offset = getMondayFirstOffset(getDay(selectedMonthDate))

  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ]

  const styles = startDateStyles()

  return (
    <View>
      {renderTrigger({ displayDate, onPress: openModal })}

      {showModal &&
        createPortal(
          <div
            role="button"
            tabIndex={0}
            className={styles.backdrop()}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowModal(false)
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setShowModal(false)
              }
            }}
          >
            <View className={styles.modal()}>
              <View className={styles.modalHeader()}>
                <Text className={styles.modalTitle()}>
                  {t('sharedFlows_common:steps.quote.startDate.modalTitle')}
                </Text>
                <Pressable onPress={() => setShowModal(false)}>
                  <Text className={styles.closeIcon()}>✕</Text>
                </Pressable>
              </View>

              <View className={styles.calendarCard()}>
                <View className={styles.monthYearRow()}>
                  <Pressable onPress={() => navigateMonth(-1)} disabled={isAtCurrentMonth}>
                    <Text
                      className={[styles.navArrow(), isAtCurrentMonth ? 'opacity-30' : ''].join(
                        ' '
                      )}
                    >
                      ‹
                    </Text>
                  </Pressable>
                  <View className={styles.fieldCard()}>
                    <Text className={styles.fieldLabel()}>
                      {t('sharedFlows_common:steps.quote.startDate.month')}
                    </Text>
                    <Text className={styles.fieldValue()}>{months[Number(selMonth) - 1]}</Text>
                  </View>
                  <View className={styles.fieldCard()}>
                    <Text className={styles.fieldLabel()}>
                      {t('sharedFlows_common:steps.quote.startDate.year')}
                    </Text>
                    <Text className={styles.fieldValue()}>{selYear}</Text>
                  </View>
                  <Pressable onPress={() => navigateMonth(1)}>
                    <Text className={styles.navArrow()}>›</Text>
                  </Pressable>
                </View>

                <View className={styles.weekdayRow()}>
                  {['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'].map((d) => (
                    <Text key={d} className={styles.weekdayLabel()}>
                      {d}
                    </Text>
                  ))}
                </View>

                <View className={styles.calendarGrid()}>
                  {Array.from({ length: offset }).map((_, i) => (
                    <View key={`e${i}`} className={styles.emptyCell()} />
                  ))}
                  {Array.from({ length: daysInMonth }).map((_, i) => {
                    const d = i + 1
                    const isPast = isBefore(createSafeDate(selYear, selMonth, d), earliestStartDate)
                    const isSel = String(d) === String(selDay)
                    const dayCellStyles = startDateStyles({ selected: isSel })
                    return (
                      <Pressable
                        key={d}
                        disabled={isPast}
                        onPress={() => setSelDay(String(d))}
                        className={[dayCellStyles.dayCell(), isPast ? 'opacity-30' : ''].join(' ')}
                      >
                        <Text className={body2({ color: isSel ? 'white' : 'deepForest' })}>
                          {d}
                        </Text>
                      </Pressable>
                    )
                  })}
                </View>
              </View>

              <Pressable
                className={styles.confirmButton()}
                onPress={() => {
                  setValue(`startDates.${productKey}`, {
                    year: selYear,
                    month: selMonth,
                    day: selDay,
                  })
                  setShowModal(false)
                }}
              >
                <Text className={styles.confirmText()}>
                  {t('sharedFlows_common:steps.quote.startDate.confirm')}
                </Text>
              </Pressable>
            </View>
          </div>,
          document.body
        )}
    </View>
  )
}

export const Header = (props: Props) => {
  const styles = headerStyles()

  return (
    <View className={styles.container()}>
      <View className={styles.quoteHeaderWrapper()}>
        <QuoteHeader
          productName={props.productName}
          headerTitle={props.headerTitle}
          headerSubtitle={props.headerSubtitle}
        />
      </View>
      <Price
        productKey={props.productKey}
        allowedPremiumIntervals={props.allowedPremiumIntervals}
      />
      <InsuranceStartDate productKey={props.productKey} />
    </View>
  )
}
